// A tiny path router: the apps have a handful of routes and need no router dependency.
import { useEffect, useState, type AnchorHTMLAttributes, type MouseEvent } from "react";

const listeners = new Set<() => void>();
export function navigate(to: string, replace = false) {
  if (replace) history.replaceState(null, "", to); else history.pushState(null, "", to);
  listeners.forEach((l) => l());
}
if (typeof window !== "undefined") window.addEventListener("popstate", () => listeners.forEach((l) => l()));

/** The address's query ("?open=…"), kept current like usePath (navigate within one page changes only this). */
export function useQuery(): string {
  const [q, setQ] = useState(() => (typeof location === "undefined" ? "" : location.search));
  useEffect(() => {
    const l = () => setQ(location.search);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return q;
}

export function usePath(): string {
  const [path, setPath] = useState(() => (typeof location === "undefined" ? "/" : location.pathname));
  useEffect(() => {
    const l = () => setPath(location.pathname);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return path;
}

/** Matches "/console/children/:id" against a path; returns params or null. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const keys: string[] = [];
  const re = new RegExp(`^${pattern.replace(/:([a-z_]+)/g, (_, k) => { keys.push(k); return "([^/]+)"; })}/?$`);
  const m = path.match(re);
  return m ? Object.fromEntries(keys.map((k, i) => [k, decodeURIComponent(m[i + 1]!)])) : null;
}

export function Link({ href, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  return (
    <a
      href={href}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0 || /^https?:/.test(href)) return;
        e.preventDefault();
        navigate(href);
      }}
      {...rest}
    />
  );
}

/** Reads a secret passed in the URL fragment (never sent to the server in logs), then clears it. */
export function takeFragment(): string | null {
  if (typeof location === "undefined" || !location.hash) return null;
  const v = decodeURIComponent(location.hash.slice(1));
  history.replaceState(null, "", location.pathname + location.search);
  return v || null;
}
